import type { BotRuntime } from "./bot-runtime";
import type { CombatRuntime } from "./combat-runtime";
import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerRuntime } from "./player-runtime";
import { gameplayConfig } from "../config/gameplay-config";
import {
  createRespawnCandidates,
  reduceRespawnLevel,
  respawnRadiusForLevel,
  selectSafeRespawnPosition,
  type RespawnSelection,
} from "../simulation/player-respawn";
import { SeededRandom } from "../simulation/seeded-random";
import type { DefeatCombatResolution } from "../simulation/size-combat";
import { recordBoundedIdentifier } from "./runtime-stability";
import { getPlayerGrowthProgress } from "../simulation/player-growth";

export type PlayerRespawnState = "inactive" | "alive" | "waiting";

export interface PlayerRespawnRecord {
  readonly contactId: string;
  readonly levelAfter: number;
  readonly levelBefore: number;
  readonly position: { readonly x: number; readonly y: number };
  readonly respawnedAtMs: number;
  readonly runId: number;
  readonly strategy: RespawnSelection["strategy"];
}

export interface PlayerDefeatRuntimeSnapshot {
  readonly lastRespawn: PlayerRespawnRecord | null;
  readonly respawnAtMs: number | null;
  readonly state: PlayerRespawnState;
}

export interface PlayerDefeatRuntimeStabilitySnapshot {
  readonly pendingRespawnCount: number;
  readonly processedEventCount: number;
}

export type IgnoredPlayerDefeatReason =
  | "duplicate-event"
  | "invalid-event"
  | "not-player-defeat"
  | "player-inactive"
  | "stale-event"
  | "unknown-winner";

export type PlayerDefeatOutcome =
  | { readonly kind: "accepted"; readonly respawnAtMs: number }
  | { readonly kind: "ignored"; readonly reason: IgnoredPlayerDefeatReason };

interface PendingRespawn {
  readonly contactId: string;
  readonly growthBefore: number;
  readonly levelBefore: number;
  readonly respawnAtMs: number;
  readonly runId: number;
}

type BotPort = Pick<BotRuntime, "getSnapshot">;
type CombatPort = Pick<CombatRuntime, "subscribe">;
type PlayerPort = Pick<
  PlayerRuntime,
  "getSnapshot" | "markDefeated" | "respawn" | "subscribeFixedStep"
>;
const cloneRecord = (record: PlayerRespawnRecord): PlayerRespawnRecord => ({
  ...record,
  position: { ...record.position },
});

export class PlayerDefeatRuntime {
  private activeRunId = 0;
  private lastRespawn: PlayerRespawnRecord | null = null;
  private readonly listeners = new Set<() => void>();
  private pending: PendingRespawn | null = null;
  private readonly processedEventKeys = new Set<string>();
  private seed: string;
  private state: PlayerRespawnState = "inactive";

  public constructor(
    private readonly flow: GameFlow,
    combatRuntime: CombatPort,
    private readonly playerRuntime: PlayerPort,
    private readonly botRuntime: BotPort,
    seed: string,
  ) {
    this.seed = seed;
    combatRuntime.subscribe((resolution) => {
      if (resolution.kind === "defeat") {
        this.consume(resolution);
      }
    });
    playerRuntime.subscribeFixedStep((_stepMilliseconds, simulationTimeMs) => {
      this.step(simulationTimeMs);
    });
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public consume(event: DefeatCombatResolution): PlayerDefeatOutcome {
    if (
      event.contactId.trim().length === 0 ||
      !Number.isSafeInteger(event.runId) ||
      event.runId <= 0 ||
      !Number.isFinite(event.observedAtMs) ||
      event.observedAtMs < 0 ||
      event.winnerId.trim().length === 0
    ) {
      return { kind: "ignored", reason: "invalid-event" };
    }
    if (event.defeatedId !== "player") {
      return { kind: "ignored", reason: "not-player-defeat" };
    }
    if (event.winnerId === "player") {
      return { kind: "ignored", reason: "invalid-event" };
    }
    const eventKey = `${String(event.runId)}:${event.contactId}`;
    if (this.processedEventKeys.has(eventKey)) {
      return { kind: "ignored", reason: "duplicate-event" };
    }
    const snapshot = this.playerRuntime.getSnapshot();
    const age = snapshot.simulationTimeMs - event.observedAtMs;
    if (
      event.runId !== this.activeRunId ||
      this.flow.getSnapshot().scene !== "gameplay" ||
      age < 0 ||
      age > gameplayConfig.combat.contactMaximumAgeMs
    ) {
      return { kind: "ignored", reason: "stale-event" };
    }
    if (
      !this.botRuntime
        .getSnapshot()
        .bots.some((bot) => bot.id === event.winnerId)
    ) {
      return { kind: "ignored", reason: "unknown-winner" };
    }
    const player = snapshot.player;
    if (player === null || !player.alive || this.pending !== null) {
      return { kind: "ignored", reason: "player-inactive" };
    }
    if (!this.playerRuntime.markDefeated()) {
      return { kind: "ignored", reason: "player-inactive" };
    }
    const respawnAtMs =
      snapshot.simulationTimeMs + gameplayConfig.combat.playerRespawnDelayMs;
    recordBoundedIdentifier(
      this.processedEventKeys,
      eventKey,
      gameplayConfig.runtimeStability.maximumProcessedEvents,
    );
    this.pending = {
      contactId: event.contactId,
      levelBefore: player.level,
      growthBefore: player.growth,
      respawnAtMs,
      runId: event.runId,
    };
    this.state = "waiting";
    this.notify();
    return { kind: "accepted", respawnAtMs };
  }

  public getSnapshot(): PlayerDefeatRuntimeSnapshot {
    return {
      lastRespawn:
        this.lastRespawn === null ? null : cloneRecord(this.lastRespawn),
      respawnAtMs: this.pending?.respawnAtMs ?? null,
      state: this.state,
    };
  }

  public getStabilitySnapshot(): PlayerDefeatRuntimeStabilitySnapshot {
    return {
      pendingRespawnCount: this.pending === null ? 0 : 1,
      processedEventCount: this.processedEventKeys.size,
    };
  }

  public setSeed(seed: string): void {
    this.seed = seed;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    listener();
    return () => {
      this.listeners.delete(listener);
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    if (snapshot.scene === "results") {
      return;
    }
    this.activeRunId =
      snapshot.scene === "gameplay"
        ? this.playerRuntime.getSnapshot().runId
        : 0;
    this.pending = null;
    this.lastRespawn = null;
    this.state = snapshot.scene === "gameplay" ? "alive" : "inactive";
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private step(simulationTimeMs: number): void {
    const pending = this.pending;
    if (pending === null || simulationTimeMs < pending.respawnAtMs) {
      return;
    }
    const player = this.playerRuntime.getSnapshot().player;
    const normalLevelAfter = reduceRespawnLevel(
      pending.levelBefore,
      gameplayConfig.combat.playerRespawnLevelLoss,
      gameplayConfig.growth.sizes.length,
    );
    const normalGrowthAfter =
      gameplayConfig.growth.thresholds[normalLevelAfter - 1];
    if (normalGrowthAfter === undefined) {
      throw new Error("Respawn growth threshold is unavailable");
    }
    const growthAfter = normalGrowthAfter;
    const levelAfter = getPlayerGrowthProgress(
      growthAfter,
      gameplayConfig.growth,
    ).level;
    if (player === null || player.alive) {
      return;
    }
    const radius = respawnRadiusForLevel(
      levelAfter,
      gameplayConfig.growth.sizes,
    );
    const random = new SeededRandom(
      `${this.seed}:player-respawn:${String(pending.runId)}:${pending.contactId}`,
    );
    const selection = selectSafeRespawnPosition({
      arena: gameplayConfig.arena,
      candidates: createRespawnCandidates(
        gameplayConfig.arena,
        gameplayConfig.combat.playerRespawnSpawnInset,
        radius,
        gameplayConfig.combat.playerRespawnGridColumns,
        gameplayConfig.combat.playerRespawnGridRows,
        random,
      ),
      fallback: gameplayConfig.movement.initialPosition,
      obstacles: this.botRuntime.getSnapshot().bots.map((bot) => ({
        position: bot.position,
        radius: bot.collision.radius,
      })),
      playerRadius: radius,
      safeDistance: gameplayConfig.combat.playerRespawnSafeDistance,
      spawnInset: gameplayConfig.combat.playerRespawnSpawnInset,
    });
    if (
      selection === null ||
      !this.playerRuntime.respawn(selection.position, levelAfter, growthAfter)
    ) {
      return;
    }
    this.lastRespawn = {
      contactId: pending.contactId,
      levelAfter,
      levelBefore: pending.levelBefore,
      position: { ...selection.position },
      respawnedAtMs: simulationTimeMs,
      runId: pending.runId,
      strategy: selection.strategy,
    };
    this.pending = null;
    this.state = "alive";
    this.notify();
  }
}
